import React, { useEffect, useMemo, useRef } from 'react';
import { MapContainer, useMap, Marker, Popup } from 'react-leaflet';
import BaseMapLayerControl from './custom/MapChangeMapLayer';
import { getCenterAndZoom } from './custom/MapFunctions';
import Cookies from 'js-cookie';
import L from 'leaflet';
import { SensorStats } from '../pages/SpeciesBirdIdentification';
import WindTurbinesDataJson from '../../assets/data/WindTurbines.json';

// Embedded SVG strings
const sensorSvg = `<svg fill="#1890ff" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
<g id="SVGRepo_bgCarrier" stroke-width="0"></g>
<g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
<g id="SVGRepo_iconCarrier">
<path d="M6.34999 6.34893L4.92999 4.92893C6.80527 3.05422 9.34835 2.00107 12 2.00107C14.6516 2.00107 17.1947 3.05422 19.07 4.92893L17.65 6.34893C16.1503 4.85283 14.1184 4.01263 12 4.01263C9.88162 4.01263 7.84972 4.85283 6.34999 6.34893Z"></path>
<path d="M9.17001 9.16893L7.76001 7.75893C8.88501 6.63533 10.41 6.00421 12 6.00421C13.59 6.00421 15.115 6.63533 16.24 7.75893L14.82 9.17893C14.0733 8.42776 13.0592 8.0034 12 7.99893C11.4746 7.99852 10.9542 8.10163 10.4686 8.30239C9.98303 8.50314 9.54176 8.79759 9.17001 9.16893Z"></path>
<path fill-rule="evenodd" clip-rule="evenodd" d="M18 10.9989H14C14 11.5294 13.7893 12.0381 13.4142 12.4131C13.0391 12.7882 12.5304 12.9989 12 12.9989C11.4696 12.9989 10.9609 12.7882 10.5858 12.4131C10.2107 12.0381 10 11.5294 10 10.9989H6C5.46957 10.9989 4.96086 11.2096 4.58579 11.5847C4.21071 11.9598 4 12.4685 4 12.9989V21.9989H20V12.9989C20 12.4685 19.7893 11.9598 19.4142 11.5847C19.0391 11.2096 18.5304 10.9989 18 10.9989ZM6 17.9989V14.9989H18V17.9989H6Z"></path>
<path d="M13 10.9989C13 11.5512 12.5523 11.9989 12 11.9989C11.4477 11.9989 11 11.5512 11 10.9989C11 10.4466 11.4477 9.99893 12 9.99893C12.5523 9.99893 13 10.4466 13 10.9989Z"></path>
</g>
</svg>`;
const turbineSvg = `<svg height="200px" width="200px" version="1.1" id="_x32_" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 512 512" xml:space="preserve" fill="#fa8c16">
<g id="SVGRepo_bgCarrier" stroke-width="0"></g>
<g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
<g id="SVGRepo_iconCarrier">
<style type="text/css">
.st0{fill:#fa8c16;}
</style>
<g>
<polygon class="st0" points="321.334,420.494 314.237,316.461 276.427,276.142 273.303,276.473 264.589,420.494 258.714,427.031 256.198,512 329.717,512 327.209,427.031"></polygon>
<path class="st0" d="M294.219,183.51c18.982,0,35.643,11.96,42.076,29.307l56.139-185.448c3.115-10.301-2.314-21.249-12.396-25.011 l-2.88-1.076c-10.082-3.746-21.354,0.98-25.74,10.81l-78.843,176.977c4.014-2.209,8.44-3.851,13.189-4.749 C288.555,183.785,291.412,183.51,294.219,183.51z"></path>
<path class="st0" d="M452.949,386.202l-113.83-156.773c-0.461,20.746-15.333,39.122-36.525,43.168 c-2.776,0.534-5.624,0.793-8.44,0.793c-2.54,0-5.032-0.218-7.476-0.631l132.538,141.326c7.363,7.849,19.549,8.625,27.842,1.764 l2.371-1.958C457.724,407.037,459.261,394.917,452.949,386.202z"></path>
<path class="st0" d="M250.04,236.873c-2.606-13.675,1.311-27.09,9.556-37.075L70.918,243.921 c-10.486,2.452-17.251,12.623-15.462,23.239l0.509,3.034c1.789,10.6,11.523,18.004,22.22,16.879l192.731-20.204 C260.332,260.428,252.5,249.739,250.04,236.873z"></path>
<path class="st0" d="M259.669,235.037c3.633,19.056,22.033,31.557,41.089,27.932c19.071-3.633,31.572-22.042,27.94-41.089 c-3.642-19.064-22.025-31.573-41.097-27.932C268.545,197.581,256.044,215.965,259.669,235.037z M290.974,211.596 c9.306-1.772,18.288,4.328,20.067,13.65c1.78,9.306-4.337,18.286-13.651,20.059c-9.305,1.772-18.278-4.337-20.059-13.634 C275.552,222.366,281.669,213.376,290.974,211.596z"></path>
</g>
</g>
</svg>`;

// Define types for the sensor data including the missing fields
interface SensorLocation {
  sensorId: string;
  name: string;
  lat: number;
  lng: number;
  altitude: number;
  distanceToNearestWindTurbine: number;
}

interface EnrichedSensor extends SensorLocation {
  stats: SensorStats;
  isActive: boolean;
}

type WindTurbine = {
  AoI: string;
  WT_Model: string;
  "WT_Power (MW)": number;
  "WT_Diameter (m)": number;
  "WT_Height (m)": number;
  X_WGS84: number;
  Y_WGS84: number;
  "Altitude (m a.s.l)": number;
};

// Sensor location data for cyprus
const CYPRUS_SENSOR_LOCATIONS: SensorLocation[] = [
  {
    sensorId: 'SMA11380',
    name: 'Sensor 01',
    lat: 34.728777,
    lng: 33.310528,
    altitude: 0,
    distanceToNearestWindTurbine: 0
  },
  {
    sensorId: 'SMA11383',
    name: 'Sensor 02',
    lat: 34.733361,
    lng: 33.298778,
    altitude: 0,
    distanceToNearestWindTurbine: 0
  },
  {
    sensorId: 'SMA11382',
    name: 'Sensor 03',
    lat: 34.736556,
    lng: 33.300611,
    altitude: 0,
    distanceToNearestWindTurbine: 0
  },
  {
    sensorId: 'SMA11369',
    name: 'Sensor 04',
    lat: 34.726944,
    lng: 33.299472,
    altitude: 0,
    distanceToNearestWindTurbine: 0
  },
];

// Greece sensor locations with altitude and distance data
const GREECE_SENSOR_LOCATIONS: SensorLocation[] = [
  {
    "sensorId": "EWB01",
    "name": "Evia_Main Stud",
    "lat": 38.083247,
    "lng": 24.57121,
    "altitude": 187,
    "distanceToNearestWindTurbine": 886.100889
  },
  {
    "sensorId": "EWB02",
    "name": "Evia_Main Stud",
    "lat": 38.081542,
    "lng": 24.56945,
    "altitude": 154,
    "distanceToNearestWindTurbine": 779.436629
  },
  {
    "sensorId": "EWB03",
    "name": "Evia_Main Stud",
    "lat": 38.083408,
    "lng": 24.566333,
    "altitude": 248,
    "distanceToNearestWindTurbine": 459.625899
  },
  {
    "sensorId": "EWB04",
    "name": "Evia_Main Stud",
    "lat": 38.083713,
    "lng": 24.569642,
    "altitude": 216,
    "distanceToNearestWindTurbine": 744.858662
  },
  {
    "sensorId": "EWB05",
    "name": "Evia_Main Stud",
    "lat": 38.083545,
    "lng": 24.563718,
    "altitude": 323,
    "distanceToNearestWindTurbine": 231.881396
  },
  {
    "sensorId": "EWB06",
    "name": "Evia_Main Stud",
    "lat": 38.082483,
    "lng": 24.555937,
    "altitude": 340,
    "distanceToNearestWindTurbine": 170.217851
  },
  {
    "sensorId": "EWB07",
    "name": "Evia_Main Stud",
    "lat": 38.086472,
    "lng": 24.551895,
    "altitude": 347,
    "distanceToNearestWindTurbine": 285.180064
  },
  {
    "sensorId": "EWB08",
    "name": "Evia_Main Stud",
    "lat": 38.0882,
    "lng": 24.54907,
    "altitude": 325,
    "distanceToNearestWindTurbine": 581.721448
  },
  {
    "sensorId": "EWB09",
    "name": "Evia_Main Stud",
    "lat": 38.087653,
    "lng": 24.547755,
    "altitude": 329,
    "distanceToNearestWindTurbine": 524.887977
  },
  {
    "sensorId": "EWB10",
    "name": "Evia_Main Stud",
    "lat": 38.085168,
    "lng": 24.535983,
    "altitude": 449,
    "distanceToNearestWindTurbine": 566.539126
  },
  {
    "sensorId": "EWB11",
    "name": "Evia_Main Stud",
    "lat": 38.08036,
    "lng": 24.554035,
    "altitude": 379,
    "distanceToNearestWindTurbine": 401.362725
  },
  {
    "sensorId": "EWB12",
    "name": "Evia_Main Stud",
    "lat": 38.08213,
    "lng": 24.552524,
    "altitude": 415,
    "distanceToNearestWindTurbine": 167.507926
  },
  {
    "sensorId": "EWC01",
    "name": "Evia_Main Stud",
    "lat": 38.078972,
    "lng": 24.534855,
    "altitude": 788,
    "distanceToNearestWindTurbine": 129.396373
  },
  {
    "sensorId": "EWC02",
    "name": "Evia_Main Stud",
    "lat": 38.077178,
    "lng": 24.526202,
    "altitude": 849,
    "distanceToNearestWindTurbine": 393.725479
  },
  {
    "sensorId": "EWC03",
    "name": "Evia_Main Stud",
    "lat": 38.091925,
    "lng": 24.511376,
    "altitude": 1020,
    "distanceToNearestWindTurbine": 52.21828
  },
  {
    "sensorId": "EWC04",
    "name": "Evia_Main Stud",
    "lat": 38.092624,
    "lng": 24.511798,
    "altitude": 1001,
    "distanceToNearestWindTurbine": 135.188912
  },
  {
    "sensorId": "ECB01",
    "name": "Evia_Control Area",
    "lat": 38.116145,
    "lng": 24.439455,
    "altitude": 274,
    "distanceToNearestWindTurbine": 1725.240922
  },
  {
    "sensorId": "ECB02",
    "name": "Evia_Control Area",
    "lat": 38.120559,
    "lng": 24.439634,
    "altitude": 266,
    "distanceToNearestWindTurbine": 1336.828483
  },
  {
    "sensorId": "ECB03",
    "name": "Evia_Control Area",
    "lat": 38.126175,
    "lng": 24.444575,
    "altitude": 304,
    "distanceToNearestWindTurbine": 1323.691846
  },
  {
    "sensorId": "ECB04",
    "name": "Evia_Control Area",
    "lat": 38.12662,
    "lng": 24.448592,
    "altitude": 374,
    "distanceToNearestWindTurbine": 1625.990656
  },
  {
    "sensorId": "ECB05",
    "name": "Evia_Control Area",
    "lat": 38.131377,
    "lng": 24.450018,
    "altitude": 252,
    "distanceToNearestWindTurbine": 1636.030171
  },
  {
    "sensorId": "ECB06",
    "name": "Evia_Control Area",
    "lat": 38.134143,
    "lng": 24.44987,
    "altitude": 209,
    "distanceToNearestWindTurbine": 1637.370009
  },
  {
    "sensorId": "TWB01",
    "name": "Thrace_Main Stud",
    "lat": 41.058091,
    "lng": 25.921683,
    "altitude": 727,
    "distanceToNearestWindTurbine": 314.381686
  },
  {
    "sensorId": "TWB02",
    "name": "Thrace_Main Stud",
    "lat": 41.060539,
    "lng": 25.922621,
    "altitude": 706,
    "distanceToNearestWindTurbine": 348.178157
  },
  {
    "sensorId": "TWB03",
    "name": "Thrace_Main Stud",
    "lat": 41.060007,
    "lng": 25.930048,
    "altitude": 637,
    "distanceToNearestWindTurbine": 358.411432
  },
  {
    "sensorId": "TWB04",
    "name": "Thrace_Main Stud",
    "lat": 41.050948,
    "lng": 25.929376,
    "altitude": 553,
    "distanceToNearestWindTurbine": 512.076463
  },
  {
    "sensorId": "TWB05",
    "name": "Thrace_Main Stud",
    "lat": 41.049685,
    "lng": 25.932351,
    "altitude": 571,
    "distanceToNearestWindTurbine": 363.089673
  },
  {
    "sensorId": "TWB06",
    "name": "Thrace_Main Stud",
    "lat": 41.048267,
    "lng": 25.9355,
    "altitude": 574,
    "distanceToNearestWindTurbine": 284.80008
  },
  {
    "sensorId": "TWB07",
    "name": "Thrace_Main Stud",
    "lat": 41.045689,
    "lng": 25.934121,
    "altitude": 549,
    "distanceToNearestWindTurbine": 516.594374
  },
  {
    "sensorId": "TWB08",
    "name": "Thrace_Main Stud",
    "lat": 41.044028,
    "lng": 25.944257,
    "altitude": 601,
    "distanceToNearestWindTurbine": 122.284798
  },
  {
    "sensorId": "TWB09",
    "name": "Thrace_Main Stud",
    "lat": 41.045837,
    "lng": 25.939608,
    "altitude": 600,
    "distanceToNearestWindTurbine": 235.200298
  },
  {
    "sensorId": "TWB10",
    "name": "Thrace_Main Stud",
    "lat": 41.039413,
    "lng": 25.94501,
    "altitude": 465,
    "distanceToNearestWindTurbine": 588.113673
  },
  {
    "sensorId": "TWB11",
    "name": "Thrace_Main Stud",
    "lat": 41.043782,
    "lng": 25.949586,
    "altitude": 457,
    "distanceToNearestWindTurbine": 565.259695
  },
  {
    "sensorId": "TWB12",
    "name": "Thrace_Main Stud",
    "lat": 41.049521,
    "lng": 25.94504,
    "altitude": 446,
    "distanceToNearestWindTurbine": 392.761401
  },
  {
    "sensorId": "TWC13",
    "name": "Thrace_Main Stud",
    "lat": 41.058344,
    "lng": 25.923505,
    "altitude": 702,
    "distanceToNearestWindTurbine": 161.451312
  },
  {
    "sensorId": "TWC14",
    "name": "Thrace_Main Stud",
    "lat": 41.053761,
    "lng": 25.933929,
    "altitude": 634,
    "distanceToNearestWindTurbine": 24.285495
  },
  {
    "sensorId": "TWC15",
    "name": "Thrace_Main Stud",
    "lat": 41.048446,
    "lng": 25.938575,
    "altitude": 603,
    "distanceToNearestWindTurbine": 33.099203
  },
  {
    "sensorId": "TWC16",
    "name": "Thrace_Main Stud",
    "lat": 41.044292,
    "lng": 25.944082,
    "altitude": 607,
    "distanceToNearestWindTurbine": 99.3206
  },
  {
    "sensorId": "TCB17",
    "name": "Thrace_Control Area",
    "lat": 41.056337,
    "lng": 25.911017,
    "altitude": 771,
    "distanceToNearestWindTurbine": 1228.676885
  },
  {
    "sensorId": "TCB18",
    "name": "Thrace_Control Area",
    "lat": 41.053098,
    "lng": 25.907388,
    "altitude": 751,
    "distanceToNearestWindTurbine": 1619.192874
  },
  {
    "sensorId": "TCB19",
    "name": "Thrace_Control Area",
    "lat": 41.04688,
    "lng": 25.906624,
    "altitude": 785,
    "distanceToNearestWindTurbine": 1466.689454
  },
  {
    "sensorId": "TCB20",
    "name": "Thrace_Control Area",
    "lat": 41.049524,
    "lng": 25.910278,
    "altitude": 766,
    "distanceToNearestWindTurbine": 1598.188671
  },
  {
    "sensorId": "TCB21",
    "name": "Thrace_Control Area",
    "lat": 41.044611,
    "lng": 25.907042,
    "altitude": 719,
    "distanceToNearestWindTurbine": 1375.945386
  },
  {
    "sensorId": "TCB22",
    "name": "Thrace_Control Area",
    "lat": 41.040709,
    "lng": 25.907261,
    "altitude": 697,
    "distanceToNearestWindTurbine": 1281.803043
  }
];

interface MapProps {
  sensorStats: Record<string, SensorStats>;
  selectedSensorId: string | null;
  hoveredSensorId: string | null;
  onSensorSelect: (sensorId: string | null) => void;
  onSensorHover: (sensorId: string | null) => void;
  location?: string | null;
}

// Map initializer to set the view based on the case study
const MapInitializer: React.FC<{
  cyprusSensors: SensorLocation[];
  greeceSensors: SensorLocation[];
  greeceLocation: string | null;
}> = ({ cyprusSensors, greeceSensors, greeceLocation }) => {
  const caseStudy = Cookies.get('caseStudy');
  const map = useMap();

  useEffect(() => {
    if (caseStudy === 'cyprus' && cyprusSensors.length > 0) {
      const { center, zoom } = getCenterAndZoom(caseStudy);
      map.setView(center, zoom);
    } else if (caseStudy === 'greece' && greeceSensors.length > 0) {
      const { center, zoom } = getCenterAndZoom(caseStudy, greeceLocation ?? undefined);
      map.setView(center, zoom);
    }
  }, [caseStudy, greeceSensors, cyprusSensors, map, greeceLocation]);

  return null;
};

// Custom marker component to handle state changes
const SensorMarker: React.FC<{
  sensor: EnrichedSensor;
  selectedSensorId: string | null;
  hoveredSensorId: string | null;
  onSensorSelect: (sensorId: string | null) => void;
  onSensorHover: (sensorId: string | null) => void;
  icon: L.Icon;
}> = ({ sensor, selectedSensorId, hoveredSensorId, onSensorSelect, onSensorHover, icon }) => {
  const markerRef = useRef<L.Marker>(null);

  const handleMarkerClick = () => {
    const newSelection = selectedSensorId === sensor.sensorId ? null : sensor.sensorId;
    onSensorSelect(newSelection);
  };

  return (
    <Marker
      ref={markerRef}
      position={[sensor.lat, sensor.lng]}
      icon={icon}
      eventHandlers={{
        click: handleMarkerClick,
        mouseover: () => onSensorHover(sensor.sensorId),
        mouseout: () => onSensorHover(null),
      }}
    >
      <Popup>
        <div style={{ minWidth: 260, fontFamily: 'Inter, Arial, sans-serif' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 8 }}>
            <div style={{ fontSize: 20 }}>{'📡'}</div>
            <div>
              <div style={{ fontWeight: 700, color: '#111' }}>{sensor.sensorId}</div>
              <div style={{ fontSize: 12, color: '#444' }}>{sensor.name}</div>
            </div>
          </div>
          <div style={{ marginTop: 8, padding: 8, background: '#f7f9fb', borderRadius: 6 }}>
            <div style={{ marginTop: 6, fontSize: 11, color: '#888' }}>
              📍 {sensor.lat.toFixed(4)}, {sensor.lng.toFixed(4)}
            </div>
            <div style={{ marginTop: 4, fontSize: 11, color: '#888' }}>
              ⛰️ Altitude: {sensor.altitude} m
            </div>
            <div style={{ marginTop: 4, fontSize: 11, color: '#888' }}>
              🌪️ Distance to Nearest Wind Turbine: {sensor.distanceToNearestWindTurbine} m
            </div>
          </div>
        </div>
      </Popup>
    </Marker>
  );
};

// Wind turbine marker
const WindTurbineMarker: React.FC<{ turbine: WindTurbine; icon: L.Icon }> = ({ turbine, icon }) => {
  // Check if coordinates are valid numbers before formatting
  const lat = typeof turbine.Y_WGS84 === 'number' ? turbine.Y_WGS84 : 0;
  const lng = typeof turbine.X_WGS84 === 'number' ? turbine.X_WGS84 : 0;

  return (
    <Marker position={[lat, lng]} icon={icon}>
      <Popup>
        <div style={{ minWidth: 180, fontFamily: 'Inter, Arial, sans-serif' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: 8 }}>
            <div style={{ fontSize: 18 }}>{'🌀'}</div>
            <div>
              <div style={{ fontWeight: 700 }}>{turbine.WT_Model}</div>
              <div style={{ fontSize: 12, color: '#666' }}>{turbine.AoI}</div>
            </div>
          </div>

          <div style={{
            marginTop: 8,
            padding: 8,
            background: '#f7f9fb',
            borderRadius: 6,
            fontSize: 11,
            color: '#555'
          }}>
            <div style={{ margin: '4px 0' }}>
              📍 {lat.toFixed(4)}, {lng.toFixed(4)}
            </div>
            <div style={{ margin: '4px 0' }}>
              ⚡ Power: {turbine["WT_Power (MW)"]} MW
            </div>
            <div style={{ margin: '4px 0' }}>
              🔄 Diameter: {turbine["WT_Diameter (m)"]} m
            </div>
            <div style={{ margin: '4px 0' }}>
              🏔️ Height: {turbine["WT_Height (m)"]} m
            </div>
            <div style={{ margin: '4px 0' }}>
              📊 Altitude: {turbine["Altitude (m a.s.l)"]} m
            </div>
          </div>
        </div>
      </Popup>
    </Marker>
  );
};

// Main component
const SpeciesBirdIdentificationMap: React.FC<MapProps> = ({
  sensorStats,
  selectedSensorId,
  hoveredSensorId,
  onSensorSelect,
  onSensorHover,
  location,
}) => {
  const caseStudy = Cookies.get('caseStudy');

  // Choose sensors based on case study and location
  const enrichedSensors: EnrichedSensor[] = useMemo(() => {
    let sensorsToUse: SensorLocation[] = [];

    if (caseStudy === 'greece' && location) {
      sensorsToUse = GREECE_SENSOR_LOCATIONS.filter((sensor) =>
        (location === 'Evia' && sensor.sensorId.startsWith('E')) || (location === 'Thrace' && sensor.sensorId.startsWith('T'))
      );
    } else if (caseStudy === 'cyprus') {
      sensorsToUse = CYPRUS_SENSOR_LOCATIONS;
    }

    return sensorsToUse.map((sensor) => ({
      ...sensor,
      stats:
        sensorStats[sensor.sensorId] ||
        ({
          sensorId: sensor.sensorId,
          totalRecordings: 0,
          uniqueSpecies: 0,
          avgProbability: 0,
          lastRecording: '',
          recordings: [],
        } as SensorStats),
      isActive: !!sensorStats[sensor.sensorId]?.totalRecordings,
    }));
  }, [caseStudy, sensorStats, location]);

  const windTurbines: WindTurbine[] = (WindTurbinesDataJson as unknown) as WindTurbine[];

  // Helper function to convert SVG to data URL
  const svgToDataUrl = (svgString: string) => {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
  };

  // Function to create Leaflet icon from SVG
  function makeSvgIcon(svgString: string, size: [number, number], fillColor: string = "#1890ff") {
    // Replace the fill color in the SVG string
    const coloredSvg = svgString.replace(/fill="#[^"]*"/g, `fill="${fillColor}"`).replace(/\.st0\{fill:#[^;}]*;?\}/g, `.st0{fill:${fillColor};}`);
    return new L.Icon({
      iconUrl: svgToDataUrl(coloredSvg),
      iconSize: size,
      iconAnchor: [Math.floor(size[0] / 2), size[1]],
      popupAnchor: [0, -size[1] + 12],
      className: 'custom-svg-icon',
    });
  }

  // Memoize icons
  const sensorIconInactive = useMemo(
    () => makeSvgIcon(sensorSvg, [28, 36], "#1890ff"),
    []
  );

  const sensorIconActive = useMemo(
    () => makeSvgIcon(sensorSvg, [34, 44], "#2eb82e"),
    []
  );

  const turbineIcon = useMemo(
    () => makeSvgIcon(turbineSvg, [40, 44], "#fa8c16"),
    []
  );

  return (
    <div style={{ position: 'relative' }}>
      <MapContainer
        id="BirdIdentificationMapId"
        center={[34.733, 33.305]}
        zoom={13}
        style={{ height: '400px', width: '100%', borderRadius: 10, border: '2px solid #eef6ff' }}
      >
        <MapInitializer
          cyprusSensors={CYPRUS_SENSOR_LOCATIONS}
          greeceSensors={GREECE_SENSOR_LOCATIONS}
          greeceLocation={location ?? null}
        />
        <BaseMapLayerControl />

        {/* Sensor markers remain unchanged */}
        {enrichedSensors.map((sensor) => (
          <SensorMarker
            key={sensor.sensorId}
            sensor={sensor}
            selectedSensorId={selectedSensorId}
            hoveredSensorId={hoveredSensorId}
            onSensorSelect={onSensorSelect}
            onSensorHover={onSensorHover}
            icon={sensor.isActive ? sensorIconActive : sensorIconInactive}
          />
        ))}

        {/* Wind turbine markers with updated popup */}
        {caseStudy === 'greece' && location &&
          windTurbines.map((turbine, index) => {
            // Skip turbines without valid coordinates
            if (!turbine.X_WGS84 || !turbine.Y_WGS84) {
              console.warn(`Skipping turbine ${index}: missing coordinates`, turbine);
              return null;
            }

            return (
              <WindTurbineMarker key={`wt-${index}`} turbine={turbine} icon={turbineIcon} />
            );
          })
        }
      </MapContainer>

      {/* Legend component remains unchanged */}
      <div
        style={{
          position: 'absolute',
          bottom: 12,
          left: 12,
          zIndex: 1000,
          backgroundColor: 'rgba(255,255,255,0.95)',
          padding: '10px 12px',
          borderRadius: 8,
          boxShadow: '0 6px 18px rgba(15,23,42,0.08)',
          fontFamily: 'Inter, Arial, sans-serif',
          fontSize: 13,
          minWidth: 160,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Legend</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <img src={svgToDataUrl(sensorSvg)} style={{ width: 20, height: 24 }} alt="Sensor icon" />
          <div style={{ lineHeight: 1 }}>Sensor Location<br /></div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src={svgToDataUrl(turbineSvg)} style={{ width: 24, height: 28 }} alt="Turbine icon" />
          <div style={{ lineHeight: 1 }}>Wind Turbine<br /></div>
        </div>
      </div>
    </div>
  );
};

export default SpeciesBirdIdentificationMap;